import React from 'react';
import {
  Box, Paper, Typography, Chip, Divider, Grid, Alert, CircularProgress, Link,
} from '@mui/material';
import {
  TrendingUp as TrendIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { TrainingInsights, DimensionKey, scoreColor, barColor } from './types';
import { ProgressBar } from '../ui/ProgressBar';
import OverviewBulletsPanel from '../ui/OverviewBulletsPanel';
import {
  InsightSection, InsightSectionId, bulletLabelsFor, sectionsFor, summaryFooterSectionsFor,
} from './insightSections';
import renderInsightSection from './renderInsightSection';
import ProcessingSection from './sections/ProcessingSection';
import { describeEndedEarly } from './endedEarly';
import AiCustomerContext from './AiCustomerContext';

interface Props {
  insights: TrainingInsights;
  /** Open the Details view at one section. */
  onViewDetails?: (sectionId: InsightSectionId) => void;
}

/** Two lines of summary at most: the card is a signpost, the detail is one click away. */
const CLAMP_TWO_LINES = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical' as const,
  overflow: 'hidden',
};

interface ScoreCardProps {
  section: InsightSection;
  insights: TrainingInsights;
  isComplete: boolean;
  onViewDetails?: (sectionId: InsightSectionId) => void;
}

/** One dimension's headline: name, score (or measure), a short line, and a way in. */
const ScoreCard: React.FC<ScoreCardProps> = ({ section, insights, isComplete, onViewDetails }) => {
  if (!isComplete) {
    return (
      <Paper variant="outlined" sx={{ p: 2, height: '100%', opacity: 0.7 }}>
        <Typography variant="subtitle2" color="text.secondary">{section.title}</Typography>
        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
          <CircularProgress size={12} />
          <Typography variant="caption" color="text.secondary">Processing…</Typography>
        </Box>
      </Paper>
    );
  }

  const score = section.score(insights);
  const valueLabel = section.valueLabel?.(insights);
  const summary = section.summary(insights);

  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1} gap={1}>
        <Typography variant="subtitle2">{section.title}</Typography>
        {score != null && <Chip label={`${score}%`} size="small" color={scoreColor(score)} />}
        {score == null && valueLabel && <Chip label={valueLabel} size="small" variant="outlined" />}
      </Box>
      {score != null && <ProgressBar value={score} tone={barColor(score)} className="mb-2" />}
      {summary && (
        <Typography variant="caption" color="text.secondary" sx={CLAMP_TWO_LINES}>
          {summary}
        </Typography>
      )}
      {onViewDetails && (
        <Box mt="auto" pt={1}>
          <Link
            component="button"
            type="button"
            variant="caption"
            underline="hover"
            onClick={() => onViewDetails(section.id)}
          >
            View details
          </Link>
        </Box>
      )}
    </Paper>
  );
};

const ScoreHeader: React.FC<{ insights: TrainingInsights; isPending: boolean }> = ({ insights, isPending }) => {
  const coaching = insights.overallCoaching;
  const endedEarly = describeEndedEarly(insights);
  return (
    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
        <Box>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="h6">Training Quality Score</Typography>
            {insights.wasAbandoned && (
              <Chip label="Session Abandoned" color="warning" size="small" variant="outlined" />
            )}
          </Box>
          {endedEarly && (
            <Typography variant="body2" fontWeight={700} data-testid="insights-ended-early">
              {endedEarly}
            </Typography>
          )}
          {coaching?.managerSummary && (
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {coaching.managerSummary}
            </Typography>
          )}
        </Box>
        {coaching ? (
          <Chip
            label={`${coaching.behavioralScore}%`}
            color={scoreColor(coaching.behavioralScore)}
            sx={{ fontSize: '1.25rem', height: 40, px: 1.5 }}
          />
        ) : isPending ? (
          <Chip
            icon={<CircularProgress size={14} sx={{ ml: 0.5 }} />}
            label="Processing…"
            variant="outlined"
            sx={{ height: 40, px: 1.5 }}
          />
        ) : null}
      </Box>
    </Paper>
  );
};

const StrengthsAndActions: React.FC<{ insights: TrainingInsights }> = ({ insights }) => {
  const coaching = insights.overallCoaching;
  if (!coaching || (!coaching.strengths?.length && !coaching.improvements?.length)) return null;
  return (
    <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
      <Grid container spacing={3}>
        {coaching.strengths?.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="success.main" gutterBottom>Strengths</Typography>
            {coaching.strengths.map((strength, idx) => (
              <Box key={idx} display="flex" gap={1} mb={0.5} alignItems="flex-start">
                <CheckIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
                <Typography variant="body2">{strength}</Typography>
              </Box>
            ))}
          </Grid>
        )}
        {coaching.improvements?.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" color="warning.main" gutterBottom>Top action items</Typography>
            {coaching.improvements.slice(0, 3).map((improvement, idx) => (
              <Box key={idx} display="flex" gap={1} mb={0.5} alignItems="flex-start">
                <TrendIcon color="warning" fontSize="small" sx={{ mt: 0.25 }} />
                <Typography variant="body2">{improvement}</Typography>
              </Box>
            ))}
          </Grid>
        )}
      </Grid>
      {coaching.recommendedNextTrack && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">Recommended next track:</Typography>
          <Typography variant="body2">{coaching.recommendedNextTrack}</Typography>
        </>
      )}
    </Paper>
  );
};

const TrainingInsightsSummary: React.FC<Props> = ({ insights, onViewDetails }) => {
  // A completed/failed session's own dimension fields are the final word,
  // even if `dimensionsComplete` is stale or absent (older sessions never
  // wrote it). Only while a session is genuinely `pending` do we trust the
  // per-dimension completion list to distinguish "not applicable" from
  // "not computed yet".
  const isPending = insights.status === 'pending';
  const isDimensionComplete = (key: DimensionKey): boolean =>
    !isPending || (insights.dimensionsComplete?.includes(key) ?? false);

  const summarySections = sectionsFor(insights).filter((section) => section.showInSummary);
  // While pending, an absent dimension may simply not be computed yet.
  const shown = summarySections.filter((section) => section.hasData(insights) || !isDimensionComplete(section.dimension));
  const notScored = summarySections.filter((section) => !shown.includes(section));
  const bullets = insights.trainingSummaryBullets ?? [];
  const footerSections = summaryFooterSectionsFor(insights).filter(
    (section) => section.hasData(insights) || !isDimensionComplete(section.dimension),
  );

  return (
    <Box>
      {insights.wasAbandoned && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Session Abandoned.</strong> The session ended before the AI customer
          finished: the rep ended it early, closed the tab or lost the connection. Items
          not attempted are listed without a score, and the overall score reflects only
          what the rep actually engaged with.
        </Alert>
      )}

      <AiCustomerContext profile={insights.aiCustomer} />

      <ScoreHeader insights={insights} isPending={isPending} />

      {bullets.length > 0 && (
        <OverviewBulletsPanel title="Session Overview" bullets={bullets} labels={bulletLabelsFor(insights)} />
      )}

      <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
        <Typography variant="h6" gutterBottom>Scores</Typography>
        <Grid container spacing={2}>
          {shown.map((section) => (
            <Grid key={section.id} size={{ xs: 12, sm: 6, md: 4 }}>
              <ScoreCard
                section={section}
                insights={insights}
                isComplete={isDimensionComplete(section.dimension)}
                onViewDetails={onViewDetails}
              />
            </Grid>
          ))}
        </Grid>
        {notScored.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
            Not part of this session: {notScored.map((section) => section.title).join(', ')}.
          </Typography>
        )}
      </Paper>

      <StrengthsAndActions insights={insights} />

      {footerSections.map((section) => (
        <React.Fragment key={section.id}>
          {isDimensionComplete(section.dimension)
            ? renderInsightSection(section.id, insights)
            : <ProcessingSection title={section.title} />}
        </React.Fragment>
      ))}
    </Box>
  );
};

export default TrainingInsightsSummary;
